import React, {PropsWithChildren, createContext, FunctionComponent, useCallback, useContext, useMemo} from "react";
import {useLocalStorage} from "../../utils/localStorageUtils";
import {isHumanSite} from "../../utils/siteUtils";

export const LIGHT_THEME = "light";
export const DARK_THEME = "dark";

export type ThemePreferences = "light" | "dark";

type ThemePreferenceContextType = {
  themePreference: ThemePreferences,
  toggleTheme: () => void,
  isLightTheme: () => boolean,
  isDarkTheme: () => boolean,
}

const ThemePreferenceContext = createContext<ThemePreferenceContextType>({
  themePreference: LIGHT_THEME,
  toggleTheme: () => {
  },
  isLightTheme: () => true,
  isDarkTheme: () => false,
});

const useThemePreference = () => useContext(ThemePreferenceContext);

// Artistree follows the OS preference until the user picks one (index.html mirrors this before first paint);
// Human always starts light and its routes switch to dark themselves.
function getDefaultThemePreference(): ThemePreferences {
  if (isHumanSite() || typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return LIGHT_THEME;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? DARK_THEME : LIGHT_THEME;
}

const ThemePreferenceProvider: FunctionComponent<PropsWithChildren> = ({children}) => {
  // Hi there 👋, thank you for reading these. If, for whatever reason, you attempt to change this, please check the index.html file.
  // At the top, it has a script that checks this before the css loads. This is done so the css reflects the theme pref and the screen doesn't flash white on site load.
  const [storedThemePreference, setThemePreference] = useLocalStorage<ThemePreferences>("theme-preference", getDefaultThemePreference());
  // anything but the two known values (a stale or hand-edited entry) would reach MUI as an unknown palette mode and crash every component
  const themePreference: ThemePreferences = storedThemePreference === DARK_THEME ? DARK_THEME : LIGHT_THEME;

  const toggleTheme = useCallback(() => {
    const newThemePref = themePreference === LIGHT_THEME ? DARK_THEME : LIGHT_THEME;
    setThemePreference(newThemePref);

    document.firstElementChild?.setAttribute('data-theme', newThemePref);
  }, [themePreference, setThemePreference]);

  const isLightTheme = useCallback(() => themePreference === LIGHT_THEME, [themePreference]);
  const isDarkTheme = useCallback(() => themePreference === DARK_THEME, [themePreference]);

  const value = useMemo(() => ({
    themePreference,
    toggleTheme,
    isLightTheme,
    isDarkTheme,
  }), [themePreference, toggleTheme, isLightTheme, isDarkTheme]);

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  )
}

export {useThemePreference, ThemePreferenceProvider}
